/**
 * ZipCheck Unified PWA — API Client
 * All API calls for guests, staff, and venue operators.
 */

const API_BASE = import.meta.env.VITE_API_URL || 'https://api.zipcheck.my'

interface ApiResponse<T> {
  data?: T
  error?: {
    code: string
    message: string
  }
}

async function request<T>(path: string, options: RequestInit = {}): Promise<ApiResponse<T>> {
  const token = localStorage.getItem('session_token')
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(options.headers as Record<string, string>),
  }
  if (token) headers['Authorization'] = `Bearer ${token}`

  const res = await fetch(`${API_BASE}${path}`, { ...options, headers })
  const data = await res.json()
  if (!res.ok) return { error: data.error || { code: 'UNKNOWN', message: 'Request failed' } }
  return { data }
}

// ─── Auth ────────────────────────────────────────────

export async function requestOtp(email: string) {
  return request<{ message: string; expiresIn: number }>('/v1/auth/request-otp', {
    method: 'POST', body: JSON.stringify({ email }),
  })
}

export async function verifyOtp(email: string, code: string) {
  return request<{ token: string; guest: { id: string; email: string; verified: boolean } }>('/v1/auth/verify-otp', {
    method: 'POST', body: JSON.stringify({ email, code }),
  })
}

export async function loginStaff(email: string, password: string) {
  return request<{ token: string; staff: { id: string; email: string; role: string; venueId: string; venueName: string } }>('/v1/auth/staff/login', {
    method: 'POST', body: JSON.stringify({ email, password }),
  })
}

export async function requestPasswordReset(email: string) {
  return request<{ message: string }>('/v1/auth/request-password-reset', {
    method: 'POST', body: JSON.stringify({ email }),
  })
}

// ─── Guest ───────────────────────────────────────────

export async function getGuestProfile() {
  return request<{
    id: string; email: string; balance: number; itemsCheckedIn: number;
    referralCode: string; vouchers: Array<{ id: string; discountPct: number; usesRemaining: number; validUntil: string }>;
    roles: Array<{ venueId: string; venueName: string; role: string }>;
  }>('/v1/guests/me')
}

export async function getBalance() {
  return request<{ amount: number; currency: string; lastTopUp: string | null }>('/v1/guests/me/balance')
}

export async function createTopUp(amount: number) {
  return request<{ clientSecret: string; topUpId: string; amount: number }>('/v1/guests/me/top-up', {
    method: 'POST', body: JSON.stringify({ amount }),
  })
}

export async function getCheckIns() {
  return request<{ checkIns: Array<{
    id: string; venueId: string; venueName: string;
    items: Array<{ id: string; type: string; tagNumber: number; status: string }>;
    totalAmount: number; status: string; createdAt: string; checkedOutAt: string | null;
  }> }>('/v1/check-ins')
}

export async function createCheckIn(venueId: string, items: Array<{ type: string }>) {
  return request<{
    checkInId: string; totalAmount: number; balanceAfter: number; qrPayload: string;
    items: Array<{ id: string; type: string; tagNumber: number }>;
  }>('/v1/check-ins', { method: 'POST', body: JSON.stringify({ venueId, items }) })
}

// ─── Wallet Pass ─────────────────────────────────────

export async function getWalletPass() {
  return request<{ passId: string; platform: string; addToWalletUrl: string; balance: number; itemsCheckedIn: number }>('/v1/guests/me/pass')
}

export async function createWalletPass() {
  return request<{ passId: string; addToWalletUrl: string; platform: string }>('/v1/guests/me/pass', { method: 'POST' })
}

// ─── Venues ──────────────────────────────────────────

export async function getVenue(slug: string) {
  return request<{
    id: string; name: string; slug: string; description: string; address: string; city: string; country: string;
    currency: string; itemTypes: Array<{ name: string; price: number }>; pricingModel: string;
    operatorName: string; operatorContact: string; operatorCountry: string;
  }>(`/v1/venues/${slug}`)
}

// ─── Staff / Check-in Operations ─────────────────────

export async function getActiveCheckIns(venueId: string) {
  return request<{ checkIns: Array<{
    id: string; guestId: string; guestName: string;
    items: Array<{ id: string; type: string; tagNumber: number; status: string }>;
    totalAmount: number; createdAt: string;
  }> }>(`/v1/venues/${venueId}/check-ins?status=active`)
}

export async function confirmCheckIn(checkInId: string) {
  return request<{ message: string }>(`/v1/check-ins/${checkInId}/confirm`, { method: 'POST' })
}

export async function checkOut(checkInId: string, itemIds?: string[]) {
  return request<{ message: string; itemsReturned: number }>(`/v1/check-ins/${checkInId}/checkout`, {
    method: 'POST', body: JSON.stringify({ itemIds }),
  })
}

export async function scanBadge(qrPayload: string) {
  return request<{ guestId: string; guestName: string; balance: number; activeCheckIns: number }>('/v1/staff/scan', {
    method: 'POST', body: JSON.stringify({ qrPayload }),
  })
}

// ─── Team Management ─────────────────────────────────

export async function getTeamMembers(venueId: string) {
  return request<{ members: Array<{
    id: string; email: string; name: string; role: string; joinedAt: string; status: string;
  }> }>(`/v1/venues/${venueId}/team`)
}

export async function addTeamMember(venueId: string, guestId: string, role: string) {
  return request<{ memberId: string; message: string }>(`/v1/venues/${venueId}/team`, {
    method: 'POST', body: JSON.stringify({ guestId, role }),
  })
}

export async function addTeamMemberByEmail(venueId: string, email: string, role: string) {
  return request<{ inviteId: string; message: string }>(`/v1/venues/${venueId}/team/invite`, {
    method: 'POST', body: JSON.stringify({ email, role }),
  })
}

export async function removeTeamMember(venueId: string, memberId: string) {
  return request<{ message: string }>(`/v1/venues/${venueId}/team/${memberId}`, { method: 'DELETE' })
}

// ─── Venue Finance ───────────────────────────────────

export async function getVenueBalance(venueId: string) {
  return request<{ available: number; pending: number; currency: string; payoutEnabled: boolean; minimumPayout: number }>(`/v1/payouts/balance?venueId=${venueId}`)
}

export async function requestPayout(venueId: string) {
  return request<{ id: string; amount: number; fee: number; status: string }>(`/v1/payouts/request`, {
    method: 'POST', body: JSON.stringify({ venueId }),
  })
}

export async function getPayouts(venueId: string) {
  return request<{ payouts: Array<{
    id: string; amount: number; fee: number; currency: string; status: string; requestedAt: string; completedAt: string | null;
  }> }>(`/v1/payouts?venueId=${venueId}`)
}

// ─── Consents ────────────────────────────────────────

export async function updateConsent(consentType: string, granted: boolean) {
  return request<{ message: string }>('/v1/guests/me/consents', {
    method: 'POST', body: JSON.stringify({ consentType, granted }),
  })
}

// ─── Support ─────────────────────────────────────────

export async function createSupportTicket(venueId: string, category: string, description: string, checkInId?: string) {
  return request<{ ticketId: string; status: string }>('/v1/support/tickets', {
    method: 'POST', body: JSON.stringify({ venueId, category, description, checkInId }),
  })
}

export async function getSupportTickets() {
  return request<{ tickets: Array<{
    id: string; venueName: string; category: string; status: string; createdAt: string; lastMessage: string;
  }> }>('/v1/support/tickets')
}

// ─── Invite ──────────────────────────────────────────

export async function getInvite(token: string) {
  return request<{ venueName: string; role: string; inviterName: string; existingUser: boolean }>(`/v1/invites/${token}`)
}

export async function acceptInvite(token: string) {
  return request<{ venueId: string; venueName: string; role: string }>('/v1/invites/accept', {
    method: 'POST', body: JSON.stringify({ token }),
  })
}
